import { useCallback, useEffect, useState } from "react";
import { AlertTriangle, AlertCircle, Info, CheckCheck } from "lucide-react";
import { Badge } from "@/components/ui/badge";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogDescription,
} from "@/components/ui/dialog";
import { cn } from "@/lib/utils";
import { ImageLightbox } from "@/components/ui/image-lightbox";
import { supabase } from "@/lib/supabase";
import { useAuth } from "@/contexts/AuthContext";
import { useGuideUpdatesForAssistant } from "@/hooks/useGuideUpdatesRealtime";
import type { GuideUpdate } from "@/hooks/useGuideUpdates";

const PRIORITY_META: Record<string, { icon: typeof Info; color: string; border: string; bg: string; label: string }> = {
  urgent: { icon: AlertCircle, label: "Urgente", color: "text-red-500", border: "border-red-300 dark:border-red-800", bg: "bg-red-50 dark:bg-red-950/30" },
  high: { icon: AlertTriangle, label: "Alta", color: "text-amber-500", border: "border-amber-300 dark:border-amber-800", bg: "bg-amber-50 dark:bg-amber-950/30" },
  normal: { icon: Info, label: "Normal", color: "text-blue-500", border: "", bg: "bg-blue-50 dark:bg-blue-950/30" },
};

const getPriority = (p: string) => PRIORITY_META[p] ?? PRIORITY_META.normal;

const formatDate = (iso: string | null) => {
  if (!iso) return null;
  return new Date(iso).toLocaleString("es", {
    day: "numeric",
    month: "short",
    year: "numeric",
    hour: "2-digit",
    minute: "2-digit",
  });
};

interface Props {
  assistantId: string | null;
}

export function GuideUpdatesMini({ assistantId }: Props) {
  const { updates, loading } = useGuideUpdatesForAssistant(assistantId);
  const { user } = useAuth();
  const userId = user?.id ?? null;
  const [readIds, setReadIds] = useState<Set<string>>(new Set());
  const [viewing, setViewing] = useState<GuideUpdate | null>(null);

  const fetchReadStatus = useCallback(async () => {
    if (!userId || updates.length === 0) return;
    const updateIds = updates.map((u) => u.id);
    const { data } = await supabase
      .from("knowledge_assistant_update_reads")
      .select("update_id")
      .eq("user_id", userId)
      .in("update_id", updateIds);
    setReadIds(new Set(((data as any[]) ?? []).map((r) => r.update_id)));
  }, [userId, updates]);

  useEffect(() => {
    void fetchReadStatus();
  }, [fetchReadStatus]);

  const handleOpen = useCallback(async (u: GuideUpdate) => {
    setViewing(u);
    if (!userId || readIds.has(u.id)) return;
    await supabase
      .from("knowledge_assistant_update_reads")
      .upsert({ user_id: userId, update_id: u.id } as any, {
        onConflict: "user_id,update_id",
      });
    setReadIds((prev) => new Set(prev).add(u.id));
  }, [userId, readIds]);

  if (loading) {
    return <p className="text-xs text-muted-foreground text-center py-4">Cargando…</p>;
  }

  if (updates.length === 0) {
    return (
      <div className="flex flex-col items-center justify-center py-4 text-center">
        <CheckCheck className="h-5 w-5 text-muted-foreground/40 mb-1" />
        <p className="text-[11px] text-muted-foreground">Sin novedades</p>
      </div>
    );
  }

  const viewingP = viewing ? getPriority(viewing.priority) : null;
  const ViewingIcon = viewingP?.icon ?? Info;

  return (
    <>
      {updates.map((u) => {
        const p = getPriority(u.priority);
        const PIcon = p.icon;
        const isRead = readIds.has(u.id);
        return (
          <div
            key={u.id}
            role="button"
            tabIndex={0}
            onClick={() => handleOpen(u)}
            onKeyDown={(e) => { if (e.key === "Enter" || e.key === " ") handleOpen(u); }}
            className={cn(
              "rounded-lg border p-2 space-y-1 cursor-pointer transition-colors hover:bg-muted/50",
              p.border,
              !isRead && "bg-primary/5",
            )}
          >
            <div className="flex items-start gap-1.5">
              <PIcon className={cn("h-3.5 w-3.5 shrink-0 mt-0.5", p.color)} />
              <div className="min-w-0 flex-1">
                <div className={cn("text-xs leading-tight", isRead ? "font-normal text-muted-foreground" : "font-semibold")}>
                  {u.title}
                </div>
                {u.description && (
                  <p className="text-[11px] text-muted-foreground mt-0.5 line-clamp-2">
                    {u.description}
                  </p>
                )}
              </div>
            </div>
            <div className="flex items-center gap-1.5 pl-5">
              <Badge variant="secondary" className="text-[9px] px-1 py-0">{p.label}</Badge>
              <span className={cn("text-[9px]", isRead ? "text-muted-foreground/60" : "text-primary font-medium")}>
                {isRead ? "Leída" : "Nueva"}
              </span>
            </div>
          </div>
        );
      })}

      <Dialog open={!!viewing} onOpenChange={(open) => !open && setViewing(null)}>
        {viewing && viewingP && (
          <DialogContent size="md">
            <DialogHeader>
              <div className="flex items-center gap-2">
                <ViewingIcon className={cn("h-5 w-5 shrink-0", viewingP.color)} />
                <DialogTitle>{viewing.title}</DialogTitle>
              </div>
              <DialogDescription className="sr-only">
                Detalle de la novedad
              </DialogDescription>
            </DialogHeader>

            <div className="space-y-3">
              {viewing.image_url && (
                <ImageLightbox
                  src={viewing.image_url}
                  className="rounded-lg max-w-full max-h-80 object-contain"
                />
              )}

              {viewing.description && (
                <div className={cn("rounded-lg p-3", viewingP.bg)}>
                  <p className="text-sm whitespace-pre-wrap">{viewing.description}</p>
                </div>
              )}

              <div className="flex flex-wrap items-center gap-2 text-xs text-muted-foreground">
                <Badge variant="secondary" className="text-[10px]">{viewingP.label}</Badge>
                {viewing.starts_at && viewing.ends_at ? (
                  <span>Vigencia: {formatDate(viewing.starts_at)} → {formatDate(viewing.ends_at)}</span>
                ) : viewing.starts_at ? (
                  <span>Desde: {formatDate(viewing.starts_at)}</span>
                ) : viewing.ends_at ? (
                  <span>Hasta: {formatDate(viewing.ends_at)}</span>
                ) : null}
              </div>
            </div>
          </DialogContent>
        )}
      </Dialog>
    </>
  );
}
